<script>
  import { createEventDispatcher, tick } from "svelte";
  import { Modal } from "svelte-utilities";
  import TextInput from "#lib/sidebar/TextInput.svelte";
  import RadioInput from "#lib/sidebar/RadioInput.svelte";
  import { applyMarkUpdate, createdDuplicates, isDuplicateMark } from "#lib/marks/transform.js";

  /**
   * Existing marks of the records being updated, in the order the new marks
   * should be applied. The caller does its own sorting and de-duplication.
   * @type {string[]}
   */
  export let marks = [];

  /**
   * Marks of every record that shares a namespace with the ones being updated
   * but is not part of this update. Used for the duplicate warning only.
   * @type {string[]}
   */
  export let otherMarks = [];

  /**
   * Reverse label order. Owned by the caller, because reversing has to happen
   * in the caller's sort — ahead of its de-duplication — to keep today's
   * behaviour when two selected frames share one record.
   *
   * One-way on purpose: the caller re-derives `marks` when this changes, and a
   * `reverse` event is the signal to do it. A two-way binding would require the
   * caller to watch this prop reactively, and a reactive statement in
   * Document.svelte that depends on the selection re-sorts that component's
   * whole reactive graph — which is what broke sheet visibility (see the
   * comment on confirmUpdateMarks there).
   */
  export let reverse = false;

  /** Hide the reverse checkbox for callers that do not order their selection. */
  export let reversible = true;

  /**
   * Mark the renumber field starts from, taken by the caller from the first
   * record in reading order. Must not come from `marks[0]`, which is reversed
   * when Reverse Label Order is on.
   */
  export let defaultStart = "";

  const dispatch = createEventDispatcher();

  const MODE_OPTIONS = [
    { value: "renumber", label: "Renumber", name: "mark-update-mode" },
    { value: "addText", label: "Add text", name: "mark-update-mode" },
    { value: "findReplace", label: "Find & replace", name: "mark-update-mode" },
  ];

  const POSITION_OPTIONS = [
    { value: "start", label: "Beginning", name: "mark-update-position" },
    { value: "end", label: "End", name: "mark-update-position" },
    { value: "anywhere", label: "Anywhere", name: "mark-update-position" },
  ];

  let modal;
  let firstInput;

  let mode = "renumber";
  let start = "";
  let prefix = "";
  let suffix = "";
  let find = "";
  let replace = "";
  let position = "start";
  let matchCase = false;

  $: options = { mode, start, prefix, suffix, find, replace, position, matchCase };
  $: newMarks = applyMarkUpdate(marks, options);
  $: rows = marks.map((from, i) => ({ from, to: newMarks[i] ?? from }));
  $: changedCount = rows.filter((r) => r.from !== r.to).length;
  $: clashes = createdDuplicates({ oldMarks: marks, newMarks, otherMarks });
  $: plural = marks.length === 1 ? "" : "s";
  // A job can carry hundreds of frames; the full list would be an unreadable
  // paragraph inside the dialog.
  $: shownClashes = clashes.slice(0, 8);
  $: hiddenClashes = clashes.length - shownClashes.length;

  export const open = async () => {
    // Renumber is the default so the dialog behaves as it always has until a
    // different mode is picked.
    mode = "renumber";
    start = defaultStart || marks[0] || "01";
    prefix = "";
    suffix = "";
    find = "";
    replace = "";
    position = "start";
    matchCase = false;

    modal.open();
    await tick();
    firstInput?.focus();
  };

  export const close = () => modal.close();
  export const isModalOpen = () => modal?.isModalOpen() ?? false;

  function confirm() {
    modal.close();
    dispatch("confirm", { marks: newMarks });
  }

  // Enter in any field applies the update, matching the old single-field dialog.
  function onEnter() {
    if (changedCount) confirm();
  }

  function toggleReverse(event) {
    reverse = event.currentTarget.checked;
    dispatch("reverse", { reverse });
  }
</script>

<Modal
  bind:this={modal}
  on:confirm={confirm}
  closeable
  width="30rem"
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Update", type: "confirm", style: "primary" },
  ]}>
  <div slot="title">
    {marks.length === 1 ? "Update Mark" : "Update Marks"}
  </div>
  <div slot="content" class="space-y-3 text-sm">
    <!-- Counts marks, not the markers clicked: several markers can share one
         record, and one record carries one mark. Naming the mark also keeps
         this honest in the legacy product, where these are openings and types
         rather than frames. -->
    <div>
      {marks.length} mark{plural} will be updated.
    </div>

    <RadioInput border label="Mode" labelWidth="5rem" options={MODE_OPTIONS} bind:value={mode} />

    {#if mode === "renumber"}
      <div class="text-gray-500">
        Marks are numbered sequentially from the starting mark. Prefix and suffix are added to every mark and
        are not incremented.
      </div>
      <TextInput
        bind:this={firstInput}
        border
        inputOnKeydown
        label={marks.length === 1 ? "Mark" : "Starting mark"}
        labelWidth="7rem"
        bind:value={start}
        on:enter={onEnter} />
      <TextInput
        border
        inputOnKeydown
        label="Prefix"
        labelWidth="7rem"
        placeholder="none"
        bind:value={prefix}
        on:enter={onEnter} />
      <TextInput
        border
        inputOnKeydown
        label="Suffix"
        labelWidth="7rem"
        placeholder="none"
        bind:value={suffix}
        on:enter={onEnter} />
      {#if reversible && marks.length > 1}
        <div class="flex items-center gap-2">
          <input type="checkbox" id="reverse-mark-update" checked={reverse} on:change={toggleReverse} />
          <label for="reverse-mark-update">Reverse Label Order</label>
        </div>
      {/if}
    {:else if mode === "addText"}
      <div class="text-gray-500">
        Existing marks are kept, including their numbering. Text is added exactly as typed, so include any
        space you want.
      </div>
      <TextInput
        bind:this={firstInput}
        border
        inputOnKeydown
        label="Prefix"
        labelWidth="7rem"
        placeholder="none"
        bind:value={prefix}
        on:enter={onEnter} />
      <TextInput
        border
        inputOnKeydown
        label="Suffix"
        labelWidth="7rem"
        placeholder="none"
        bind:value={suffix}
        on:enter={onEnter} />
    {:else}
      <div class="text-gray-500">
        Only the matched text changes. Marks without a match are left alone, and an empty replacement removes
        the matched text.
      </div>
      <TextInput
        bind:this={firstInput}
        border
        inputOnKeydown
        label="Find"
        labelWidth="7rem"
        bind:value={find}
        on:enter={onEnter} />
      <TextInput
        border
        inputOnKeydown
        label="Replace with"
        labelWidth="7rem"
        placeholder="remove"
        bind:value={replace}
        on:enter={onEnter} />
      <RadioInput
        border
        label="Match at"
        labelWidth="5rem"
        options={POSITION_OPTIONS}
        bind:value={position} />
      <div class="flex items-center gap-2">
        <input type="checkbox" id="mark-update-match-case" bind:checked={matchCase} />
        <label for="mark-update-match-case">Match case</label>
      </div>
    {/if}

    <div class="border-t pt-2 space-y-2">
      <div class="flex items-baseline justify-between">
        <div class="text-gray-500">Preview</div>
        <div class="text-xs text-gray-500">
          {changedCount} of {marks.length} will change
        </div>
      </div>
      <div class="max-h-40 overflow-y-auto preview">
        {#each rows as row, i (i)}
          {@const unchanged = row.from === row.to}
          <div class="preview-row" class:unchanged>
            <div class="truncate">{row.from || "(no mark)"}</div>
            <div class="arrow">&rarr;</div>
            <div class="truncate">{row.to || "(no mark)"}</div>
            <!-- A collision outranks "unchanged": a row that was already
                 clashing before this update is still worth flagging. -->
            {#if isDuplicateMark(clashes, row.to)}
              <div class="tag warn">duplicate</div>
            {:else if unchanged}
              <div class="tag">unchanged</div>
            {:else}
              <div></div>
            {/if}
          </div>
        {/each}
      </div>
      {#if clashes.length}
        <div class="text-amber-700">
          This would put {clashes.length} mark{clashes.length === 1 ? "" : "s"} on more than one record: {shownClashes.join(
            ", ",
          )}{#if hiddenClashes > 0}&nbsp;and {hiddenClashes} more{/if}
        </div>
      {/if}
      {#if !changedCount}
        <div class="text-gray-500">No marks would change.</div>
      {/if}
    </div>
  </div>
</Modal>

<style lang="scss">
  .preview {
    @apply rounded ring-1 ring-inset ring-gray-200 divide-y divide-gray-100;
  }

  .preview-row {
    @apply grid items-baseline gap-2 px-2 py-1 font-mono text-xs;
    grid-template-columns: 1fr auto 1fr 5rem;

    &.unchanged {
      @apply text-gray-400;
    }

    .arrow {
      @apply text-gray-400 flex-none;
    }

    .tag {
      @apply text-right font-sans text-gray-400;

      &.warn {
        @apply text-amber-700;
      }
    }
  }
</style>
